/**
 * list.ts - Tournament List Display and Management
 *
 * This module handles:
 * - Loading and displaying tournaments
 * - Managing tournament cards in the UI
 * - Setting up event listeners for tournament interactions
 */

import type { Tournament } from '../../types/api';
import { fetchTournaments } from '../api/tournament-api';
import { createTournamentCard, createTournamentListCard, initializeTournamentMap } from './card';
import { saveUIState, restoreUIState } from '../utils/state';
import { t } from '../utils/i18n-helper';
import { websocketClient } from '../websocket/websocket-client';
import { getCurrentUser } from '../auth/auth-state';

/**
 * Initialize WebSocket real-time updates for tournaments
 */
export function initializeWebSocketUpdates(): void {
  websocketClient.onUpdate((payload) => {
    console.log('[Tournament] Received real-time update:', payload);

    const { tournamentId, updateType } = payload;

    // Refresh standings when results are updated
    if (updateType === 'result' || updateType === 'general') {
      // Update standings tab if visible
      const standingsTab = document.getElementById(`standings-${tournamentId}`);
      if (standingsTab && window.loadStandings) {
        window.loadStandings(tournamentId);
      }

      // Refresh pairings display
      if (window.refreshTournamentPairings) {
        window.refreshTournamentPairings(tournamentId);
      }
    }

    // For general updates (like new round generated), refresh everything
    if (updateType === 'general') {
      // Also reload the matches/pairings tab
      if (window.refreshTournamentPairings) {
        window.refreshTournamentPairings(tournamentId);
      }
    }

    // Refresh pending registrations when new registration is received
    if (updateType === 'registration') {
      if (window.loadPendingRegistrations && getCurrentUser()) {
        console.log('[Tournament] Refreshing pending registrations for tournament:', tournamentId);
        window.loadPendingRegistrations(tournamentId);
      }
    }
  });

  console.log('[Tournament] WebSocket real-time updates initialized');
}

/**
 * Load a single demo tournament (for non-logged-in users)
 * @param tournamentId - Tournament ID
 */
async function loadDemoTournament(tournamentId: string): Promise<void> {
  console.log('[Demo] Loading demo tournament:', tournamentId);

  try {
    const response = await fetch(`/api/tournaments/${tournamentId}`, {
      credentials: 'include'
    });

    console.log('[Demo] Response status:', response.status);

    if (!response.ok) {
      throw new Error('Failed to fetch demo tournament');
    }

    const tournament = await response.json();
    console.log('[Demo] Tournament loaded:', tournament);

    // Verify it's a demo tournament
    if (!tournament.isDemo) {
      throw new Error('Not a demo tournament');
    }

    // Display the tournament
    displayTournaments([tournament]);

    // Auto-expand the tournament
    setTimeout(() => {
      const toggleIcon = document.getElementById(`tournament-toggle-icon-${tournamentId}`);
      if (toggleIcon && toggleIcon.textContent === '▶') {
        const content = document.getElementById(`tournament-content-${tournamentId}`);
        if (content) {
          content.style.display = 'block';
          toggleIcon.textContent = '▼';
        }
      }
    }, 100);

    // Show banner for non-logged-in demo users
    const currentUser = getCurrentUser();
    if (!currentUser) {
      showDemoBanner();
    }
  } catch (error) {
    console.error('Error loading demo tournament:', error);
    const container = document.getElementById('tournaments-container');
    if (container) {
      container.innerHTML = `<p class="error-message">${t('landing.demo.error', 'Failed to load demo tournament. Please try again.')}</p>`;
    }
  }
}

/**
 * Show a persistent banner for demo tournaments
 * @param containerId - Container ID to insert banner before
 * @param showSignup - Whether to show signup buttons (for list view) or just the warning
 */
function showDemoBanner(containerId: string = 'tournaments-container', showSignup: boolean = true): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  // Remove any existing demo banner
  const existingBanner = document.getElementById('demo-tournament-banner');
  if (existingBanner) existingBanner.remove();

  const banner = document.createElement('div');
  banner.id = 'demo-tournament-banner';
  banner.className = 'demo-tournament-banner';
  banner.innerHTML = `
    <div class="demo-banner-content">
      <div class="demo-banner-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <div class="demo-banner-text">
        <p class="demo-banner-title">${t('landing.demo.banner.warningTitle', 'Demo Mode - This tournament will not be saved')}</p>
        <p class="demo-banner-subtitle">${t('landing.demo.banner.warningSubtitle', 'Changes are temporary and will be lost when you leave. Create an account to save your tournaments.')}</p>
      </div>
      ${showSignup ? `
        <div class="demo-banner-actions">
          <button class="btn btn-primary btn-sm" onclick="window.openAuthModal && window.openAuthModal('register')">
            ${t('landing.demo.banner.signup', 'Sign Up Free')}
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.location.href='/'">
            ${t('landing.demo.banner.back', 'Back to Home')}
          </button>
        </div>
      ` : ''}
    </div>
  `;

  // Insert at the top of the main content area
  const main = container.closest('main');
  if (main) {
    main.insertBefore(banner, main.firstChild);
  } else {
    container.parentElement?.insertBefore(banner, container);
  }
}

/**
 * Display tournaments in the container
 * @param tournaments - Array of tournament objects
 */
export function displayTournaments(tournaments: Tournament[]): void {
  console.log('[Tournament] displayTournaments called with', tournaments.length, 'tournaments');
  const container = document.getElementById('tournaments-container');

  console.log('[Tournament] Container:', container);

  if (!container) {
    console.error('[Tournament] Container not found!');
    return;
  }

  if (tournaments.length === 0) {
    container.innerHTML = `<p class="empty-state" data-i18n="tournament.noTournaments">${t('tournament.noTournaments', 'No tournaments yet. Create one to get started!')}</p>`;
    return;
  }

  const cardsHtml = tournaments.map(tournament => createTournamentCard(tournament)).join('');
  console.log('[Tournament] Generated HTML length:', cardsHtml.length);

  container.innerHTML = cardsHtml;
  console.log('[Tournament] HTML inserted into container');

  // Subscribe to WebSocket updates for all tournaments
  tournaments.forEach(tournament => {
    websocketClient.subscribeTournament(tournament.id);
  });

  // Add event listeners for action buttons
  tournaments.forEach(tournament => {
    // Note: Delete, Edit, and Add Player buttons use inline onclick handlers in the HTML
    // So we don't need to add event listeners for them here

    const addPlayerBtn = document.getElementById(`add-player-${tournament.id}`);
    if (addPlayerBtn && window.addPlayer) {
      addPlayerBtn.addEventListener('click', () => window.addPlayer(tournament.id));
    }

    // Setup player search functionality
    const playerSearchInput = document.getElementById(`player-search-${tournament.id}`);
    if (playerSearchInput && window.setupPlayerSearch) {
      window.setupPlayerSearch(tournament.id);
    }

    // Setup field autocomplete for lastName and club fields
    if (window.setupFieldAutocompletes) {
      window.setupFieldAutocompletes(tournament.id);
    }

    // Setup Enter key handlers for player form inputs
    if (window.setupPlayerFormKeyHandlers) {
      window.setupPlayerFormKeyHandlers(tournament.id);
    }

    // Note: Generate pairings button uses onclick attribute, no need for addEventListener

    // Setup tab switching
    const tabButtons = document.querySelectorAll(`[data-tab^="matches-${tournament.id}"], [data-tab^="standings-${tournament.id}"], [data-tab^="ranking-${tournament.id}"]`);
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.getAttribute('data-tab');
        if (targetTab && window.switchTab) {
          window.switchTab(tournament.id, targetTab);
        }
      });
    });

    // Auto-load standings (shows initial ranking if no rounds yet)
    if (window.loadStandings) {
      window.loadStandings(tournament.id);
    }

    // Auto-open sections that should be open (e.g., after publishing)
    if (window.autoOpenSections) {
      window.autoOpenSections(tournament.id);
    }

    // Initialize map if tournament has coordinates
    if (tournament.latitude && tournament.longitude) {
      initializeTournamentMap(tournament.id, tournament.latitude, tournament.longitude, tournament.venue);
    }

    // Load pending registrations for this tournament (only if authenticated)
    if (window.loadPendingRegistrations && getCurrentUser()) {
      window.loadPendingRegistrations(tournament.id);
    }
  });
}

// Store tournaments for filtering
let allTournaments: Tournament[] = [];

/**
 * Display tournaments as a simplified list (for mytournaments.html)
 * @param tournaments - Array of tournament objects
 */
export function displayTournamentsList(tournaments: Tournament[]): void {
  console.log('[Tournament] displayTournamentsList called with', tournaments.length, 'tournaments');

  // Store tournaments for filtering
  allTournaments = tournaments;

  // Apply current filters
  const filteredTournaments = applyTournamentsListFilters(tournaments);

  const container = document.getElementById('tournaments-container');
  const countEl = document.getElementById('tournaments-count');

  if (!container) {
    console.error('[Tournament] Container not found!');
    return;
  }

  // Update count
  if (countEl) {
    countEl.textContent = filteredTournaments.length === 1
      ? t('tournament.tournamentFound', '{count} tournament', { count: filteredTournaments.length })
      : t('tournament.tournamentsFound', '{count} tournaments', { count: filteredTournaments.length });
  }

  if (filteredTournaments.length === 0) {
    // Check if there are no tournaments at all (vs just filtered out)
    if (allTournaments.length === 0) {
      // Show welcome card for users with no tournaments
      container.innerHTML = `
        <div class="guest-welcome-message">
          <div class="guest-welcome-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <h2>${t('tournament.welcome.title', 'Create Your First Tournament')}</h2>
          <p>${t('tournament.welcome.subtitle', 'Get started by creating a tournament. You can manage players, generate pairings, and track standings.')}</p>
          <div class="guest-welcome-actions">
            <button class="btn btn-primary btn-large" onclick="window.showCreateTournament && window.showCreateTournament()">
              ${t('guest.welcome.createTournament', 'Create Tournament')}
            </button>
          </div>
        </div>
      `;
    } else {
      // Tournaments exist but are filtered out
      container.innerHTML = `<p class="empty-state" data-i18n="tournament.noTournamentsFiltered">${t('tournament.noTournamentsFiltered', 'No tournaments found matching your filters.')}</p>`;
    }
    return;
  }

  const cardsHtml = filteredTournaments.map(tournament => createTournamentListCard(tournament)).join('');
  container.innerHTML = cardsHtml;

  // No need for event listeners or subscriptions on the list view
  // Users will navigate to the detail page by clicking the card
}

/**
 * Apply filters to tournaments list
 * @param tournaments - Array of tournament objects
 * @returns Filtered tournaments
 */
function applyTournamentsListFilters(tournaments: Tournament[]): Tournament[] {
  const searchInput = document.getElementById('tournament-search') as HTMLInputElement | null;
  const statusFilter = document.getElementById('status-filter') as HTMLSelectElement | null;
  const formatFilter = document.getElementById('format-filter') as HTMLSelectElement | null;

  let filtered = [...tournaments];

  // Search by name
  if (searchInput && searchInput.value.trim()) {
    const searchTerm = searchInput.value.trim().toLowerCase();
    filtered = filtered.filter(t =>
      t.name.toLowerCase().includes(searchTerm) ||
      (t.venue && t.venue.toLowerCase().includes(searchTerm))
    );
  }

  // Filter by status
  if (statusFilter && statusFilter.value) {
    filtered = filtered.filter(t => t.status === statusFilter.value);
  }

  // Filter by format
  if (formatFilter && formatFilter.value) {
    filtered = filtered.filter(t => t.format === formatFilter.value);
  }

  return filtered;
}

/**
 * Setup search and filter event listeners for tournaments list
 */
export function setupTournamentsListFilters(): void {
  const searchInput = document.getElementById('tournament-search') as HTMLInputElement | null;
  const statusFilter = document.getElementById('status-filter') as HTMLSelectElement | null;
  const formatFilter = document.getElementById('format-filter') as HTMLSelectElement | null;

  // Debounced search
  let debounceTimer: number | undefined;

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        displayTournamentsList(allTournaments);
      }, 300);
    });
  }

  // Immediate filter on change
  if (statusFilter) {
    statusFilter.addEventListener('change', () => {
      displayTournamentsList(allTournaments);
    });
  }

  if (formatFilter) {
    formatFilter.addEventListener('change', () => {
      displayTournamentsList(allTournaments);
    });
  }
}

/**
 * Event handler for standings round selector buttons (using event delegation)
 * @param e - Click event
 */
export function handleStandingsRoundClick(e: Event): void {
  const target = e.target as HTMLElement;

  // Check if clicked element is a standings round button
  if (target && target.classList.contains('standings-round-btn')) {
    e.preventDefault();
    e.stopPropagation();

    const tournamentId = target.getAttribute('data-tournament');
    const roundStr = target.getAttribute('data-round');
    const type = target.getAttribute('data-type');

    if (!tournamentId) return;

    if (type === 'ranking') {
      // Load initial ranking
      if (window.loadRankingInStandings) {
        window.loadRankingInStandings(tournamentId);
      }
    } else if (roundStr) {
      // Load standings for the selected round
      const round = parseInt(roundStr);
      if (window.loadStandingsForRound) {
        window.loadStandingsForRound(tournamentId, round);
      }
    }
  }
}

/**
 * Filter tournaments based on selected status filters
 */
export function filterTournaments(): void {
  const upcomingCheckbox = document.getElementById('filter-upcoming') as HTMLInputElement | null;
  const ongoingCheckbox = document.getElementById('filter-ongoing') as HTMLInputElement | null;
  const pastCheckbox = document.getElementById('filter-past') as HTMLInputElement | null;

  if (!upcomingCheckbox || !ongoingCheckbox || !pastCheckbox) return;

  const showUpcoming = upcomingCheckbox.checked;
  const showOngoing = ongoingCheckbox.checked;
  const showPast = pastCheckbox.checked;

  // Get all tournament cards
  const tournamentCards = document.querySelectorAll<HTMLElement>('.tournament-card');

  tournamentCards.forEach(card => {
    const statusElement = card.querySelector<HTMLElement>('.tournament-status');
    if (!statusElement) return;

    const status = statusElement.getAttribute('data-status');

    // Determine if this tournament should be visible
    let shouldShow = false;
    if (status === 'upcoming' && showUpcoming) shouldShow = true;
    if (status === 'ongoing' && showOngoing) shouldShow = true;
    if (status === 'completed' && showPast) shouldShow = true;

    // Show or hide the card
    card.style.display = shouldShow ? 'block' : 'none';
  });
}

/**
 * Toggle all tournament content visibility (expand/collapse all)
 */
export function toggleAllTournaments(): void {
  const tournamentCards = document.querySelectorAll<HTMLElement>('.tournament-card');
  const toggleButton = document.getElementById('toggle-all-text');

  if (tournamentCards.length === 0) return;

  // Check if any tournaments are expanded
  let anyExpanded = false;
  tournamentCards.forEach(card => {
    const tournamentId = card.querySelector('.tournament-title')?.getAttribute('onclick')?.match(/'([^']+)'/)?.[1];
    if (tournamentId) {
      const content = document.getElementById(`tournament-content-${tournamentId}`);
      if (content && content.style.display !== 'none') {
        anyExpanded = true;
      }
    }
  });

  // If any are expanded, collapse all. Otherwise, expand all.
  const shouldExpand = !anyExpanded;

  tournamentCards.forEach(card => {
    const tournamentId = card.querySelector('.tournament-title')?.getAttribute('onclick')?.match(/'([^']+)'/)?.[1];
    if (tournamentId) {
      const content = document.getElementById(`tournament-content-${tournamentId}`);
      const toggleIcon = document.getElementById(`tournament-toggle-icon-${tournamentId}`);

      if (content && toggleIcon) {
        if (shouldExpand) {
          content.style.display = 'block';
          toggleIcon.textContent = '▼';
        } else {
          content.style.display = 'none';
          toggleIcon.textContent = '▶';
        }
      }
    }
  });

  // Update button text
  if (toggleButton) {
    if (shouldExpand) {
      toggleButton.setAttribute('data-i18n', 'tournament.collapseAll');
      toggleButton.textContent = t('tournament.collapseAll', 'Collapse All');
    } else {
      toggleButton.setAttribute('data-i18n', 'tournament.expandAll');
      toggleButton.textContent = t('tournament.expandAll', 'Expand All');
    }
  }
}

/**
 * Load tournaments for the list view (simplified cards)
 */
export async function loadTournamentsList(): Promise<void> {
  console.log('[Tournament] loadTournamentsList called');

  try {
    const tournaments = await fetchTournaments();
    displayTournamentsList(tournaments);

    // Setup filters (only once)
    setupTournamentsListFilters();
  } catch (error) {
    console.error('Error loading tournaments list:', error);
  }
}

/**
 * Load a single tournament for the tournament detail page
 * @param tournamentId - Tournament ID to load
 */
export async function loadSingleTournament(tournamentId: string): Promise<void> {
  console.log('[Tournament] loadSingleTournament called for ID:', tournamentId);

  const container = document.getElementById('tournament-container');
  if (!container) {
    console.error('[Tournament] Container not found!');
    return;
  }

  try {
    const response = await fetch(`/api/tournaments/${tournamentId}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch tournament');
    }

    const tournament = await response.json();
    console.log('[Tournament] Tournament loaded:', tournament);

    // Check for saved active tab BEFORE rendering
    const activeTab = sessionStorage.getItem(`active-tab-${tournament.id}`);
    console.log('[Tournament] Active tab from storage:', activeTab);
    if (activeTab) {
      sessionStorage.removeItem(`active-tab-${tournament.id}`);
    }

    // Store tournament for access by other modules
    (window as any).currentTournament = tournament;

    // Display the tournament using the full card
    const cardHtml = createTournamentCard(tournament);
    container.innerHTML = cardHtml;

    // Add "My Tournaments" button to header
    const headerExtraControls = document.getElementById('header-extra-controls');
    if (headerExtraControls) {
      headerExtraControls.innerHTML = `
        <button type="button" id="my-tournaments-btn" class="my-tournaments-btn" onclick="window.location.href='/mytournaments.html'">
          ${t('context.myTournaments', 'My Tournaments')}
        </button>
      `;
    }

    // Apply active tab immediately after DOM is set (no flicker)
    if (activeTab && window.switchMainTab) {
      console.log('[Tournament] Switching to tab:', activeTab);
      window.switchMainTab(tournament.id, activeTab);
    }

    // Subscribe to WebSocket updates
    websocketClient.subscribeTournament(tournament.id);

    // Setup event listeners and initialize components
    const addPlayerBtn = document.getElementById(`add-player-${tournament.id}`);
    if (addPlayerBtn && window.addPlayer) {
      addPlayerBtn.addEventListener('click', () => window.addPlayer(tournament.id));
    }

    const playerSearchInput = document.getElementById(`player-search-${tournament.id}`);
    if (playerSearchInput && window.setupPlayerSearch) {
      window.setupPlayerSearch(tournament.id);
    }

    // Setup field autocomplete for lastName and club fields
    if (window.setupFieldAutocompletes) {
      window.setupFieldAutocompletes(tournament.id);
    }

    if (window.setupPlayerFormKeyHandlers) {
      window.setupPlayerFormKeyHandlers(tournament.id);
    }

    const tabButtons = document.querySelectorAll(`[data-tab^="matches-${tournament.id}"], [data-tab^="standings-${tournament.id}"], [data-tab^="ranking-${tournament.id}"]`);
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.getAttribute('data-tab');
        if (targetTab && window.switchTab) {
          window.switchTab(tournament.id, targetTab);
        }
      });
    });

    if (window.loadStandings) {
      window.loadStandings(tournament.id);
    }

    if (window.autoOpenSections) {
      window.autoOpenSections(tournament.id);
    }

    if (tournament.latitude && tournament.longitude) {
      initializeTournamentMap(tournament.id, tournament.latitude, tournament.longitude, tournament.venue);
    }

    if (window.loadPendingRegistrations && getCurrentUser()) {
      window.loadPendingRegistrations(tournament.id);
    }

    // Initialize Stripe Connect UI if tournament has Stripe connected
    if (tournament.stripeConnectedAccountId && window.initStripeConnectUI) {
      window.initStripeConnectUI(tournament.id);
    }

    // Update player category badges
    if (window.updatePlayerCategoryBadges) {
      window.updatePlayerCategoryBadges(tournament.id);
    }

    // Initialize categories in add player form
    if (window.initAddPlayerCategories) {
      window.initAddPlayerCategories(tournament.id);
    }

    // Initialize category filter pills for player list
    if (window.initPlayerCategoryFilter) {
      window.initPlayerCategoryFilter(tournament.id);
    }

    // Show demo banner for demo tournaments
    if (tournament.isDemo) {
      showDemoBanner('tournament-container', true);
    }
  } catch (error) {
    console.error('Error loading tournament:', error);
    container.innerHTML = `<p class="error-message">${t('tournament.error', 'Failed to load tournament. Please try again.')}</p>`;
  }
}

// Export to window for global access

window.displayTournaments = displayTournaments;
window.displayTournamentsList = displayTournamentsList;
window.loadTournamentsList = loadTournamentsList;
window.loadSingleTournament = loadSingleTournament;
window.handleStandingsRoundClick = handleStandingsRoundClick;
window.filterTournaments = filterTournaments;
window.toggleAllTournaments = toggleAllTournaments;
